import React from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Calendar, LogOut, ShoppingBag, FileText, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

export const DashboardPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (!user) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#8B5A8B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const menuItems = [
    { icon: ShoppingBag, label: 'Mes commandes', desc: 'Voir l\'historique de mes achats', active: true },
    { icon: FileText, label: 'Mes projets', desc: 'Suivre l\'avancement de mes projets', active: false },
    { icon: Settings, label: 'Paramètres', desc: 'Gérer mon compte', active: false },
  ];

  return (
    <div className="min-h-screen pt-24" data-testid="dashboard-page">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card rounded-3xl p-8 mb-8"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-[#8B5A8B]/20 rounded-full flex items-center justify-center">
                <User className="text-[#8B5A8B]" size={32} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white" data-testid="dashboard-username">
                  {user.name}
                </h1>
                <p className="text-white/60 flex items-center gap-2">
                  <Mail size={14} />
                  {user.email}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="outline-btn flex items-center gap-2 w-fit"
              data-testid="dashboard-logout"
            >
              <LogOut size={18} />
              Déconnexion
            </button>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid md:grid-cols-3 gap-6 mb-8"
        >
          <div className="glass-card rounded-2xl p-6">
            <ShoppingBag className="text-[#8B5A8B] mb-3" size={24} />
            <p className="text-2xl font-bold text-white">0</p>
            <p className="text-white/60 text-sm">Commandes</p>
          </div>
          <div className="glass-card rounded-2xl p-6">
            <FileText className="text-[#8B5A8B] mb-3" size={24} />
            <p className="text-2xl font-bold text-white">0</p>
            <p className="text-white/60 text-sm">Projets en cours</p>
          </div>
          <div className="glass-card rounded-2xl p-6">
            <Calendar className="text-[#8B5A8B] mb-3" size={24} />
            <p className="text-2xl font-bold text-white">
              {new Date(user.created_at).toLocaleDateString('fr-FR')}
            </p>
            <p className="text-white/60 text-sm">Membre depuis</p>
          </div>
        </motion.div>

        {/* Menu */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          <h2 className="text-xl font-bold text-white mb-4">Mon espace</h2>
          {menuItems.map((item, index) => (
            <button
              key={index}
              className={`w-full glass-card rounded-2xl p-6 text-left flex items-center gap-4 transition-all ${
                item.active ? 'hover:border-[#8B5A8B]/50' : 'opacity-50 cursor-not-allowed'
              }`}
              disabled={!item.active}
              data-testid={`dashboard-menu-${index}`}
            >
              <div className="w-12 h-12 bg-[#8B5A8B]/20 rounded-xl flex items-center justify-center">
                <item.icon className="text-[#8B5A8B]" size={24} />
              </div>
              <div>
                <p className="text-white font-medium">{item.label}</p>
                <p className="text-white/60 text-sm">{item.desc}</p>
              </div>
              {!item.active && (
                <span className="ml-auto text-xs text-white/40 bg-white/5 px-3 py-1 rounded-full">
                  Bientôt
                </span>
              )}
            </button>
          ))}
        </motion.div>

        {/* Empty state for orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card rounded-3xl p-12 text-center mt-8"
        >
          <ShoppingBag className="text-white/20 mx-auto mb-4" size={48} />
          <h3 className="text-xl font-semibold text-white mb-2">
            Aucune commande pour le moment
          </h3>
          <p className="text-white/60 mb-6">
            Découvrez nos solutions digitales et passez votre première commande.
          </p>
          <a href="/tarifs" className="glow-btn inline-block">
            Voir nos offres
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;
